import { useActiveAddress } from "@arweave-wallet-kit/react"
import {
  Box,
  Button,
  Chip,
  Divider,
  Grid,
  Skeleton,
  Stack,
  Theme,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material"

import { useQuery } from "@tanstack/react-query"
import numbro from "numbro"
import { useEffect, useState } from "react"

import { Link, Navigate } from "react-router-dom"

import { getPrimaryName } from "../ao/arns-api"
import { PageWrapper } from "../components/PageWrapper"
import SearchPool from "../components/SearchPool"
import {
  StatsWidget,
  StatsWidgetChange,
  StatsWidgetLabel,
  StatsWidgetValue,
} from "../components/StatsWidget"
import PoolsStack from "../components/my-account/PoolsStack"
import TotalAssetsValueChart from "../components/my-account/TotalAssetsChart"
import usePoolPnlHistory from "../hooks/use-pool-pnl-history"
import { shortHash } from "../utils/hash"

export default function MyAccount() {
  useEffect(() => {
    document.title = "My Account · Botega"
  }, [])

  const [search, setSearch] = useState("")
  const connectedWallet = useActiveAddress() as string
  const theme = useTheme()
  const isMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down("sm"))
  const { data, isLoading } = usePoolPnlHistory()

  const [connectionTimedOut, setConnectionTimedOut] = useState(false)

  useEffect(() => {
    // we need to wait a little bit before useActiveAddress recovers the current connection
    const timeout = setTimeout(() => {
      setConnectionTimedOut(true)
    }, 5_000)

    return () => clearTimeout(timeout)
  }, [])

  const { data: arnsName } = useQuery({
    queryKey: ["arns_name", connectedWallet],
    queryFn: () => getPrimaryName(connectedWallet),
    enabled: !!connectedWallet,
  })

  if (!connectedWallet && connectionTimedOut) {
    return <Navigate to={`/pools`} />
  }

  if (isLoading || !data) {
    return <Skeleton height="328px" width="100%" />
  }

  const totalPnlFromPools = data.pools.reduce((acc, pool) => acc + pool.pnl, 0)

  const last24HPnlChange = data.pools.reduce((acc, pool) => {
    const currentValue = pool.historical_pnl[pool.historical_pnl.length - 1]?.pnl_user || 0
    const oldValue = pool.historical_pnl[pool.historical_pnl.length - 2]?.pnl_user || 0

    const diff = currentValue - oldValue
    return acc + diff
  }, 0)
  const totalAssetsValue = data.pools.reduce((acc, pool) => acc + pool.current_user_tvl, 0)

  const averagePnl = data.pools.reduce((acc, pool) => acc + pool.pnl_30d_ago, 0) || 0

  const averagePnlPercentage =
    data.pools.reduce((acc, pool) => acc + pool.pnl_30d_percentage, 0) / data.pools.length || 0

  return (
    <PageWrapper>
      <Stack direction="column" spacing={6}>
        <Stack spacing={4}>
          <Stack
            direction={isMobile ? "column" : "row"}
            justifyContent="center"
            alignItems="center"
            spacing={2}
          >
            <Link to="/pools">
              <Button
                variant="contained"
                color="secondary"
                sx={() => ({
                  color: "#fff",
                  borderRadius: "4px",
                  padding: "1.15rem 2.5rem",
                  gap: "0.5rem",
                  width: isMobile ? "100%" : "auto",
                })}
              >
                Explore All Pools
              </Button>
            </Link>
          </Stack>

          <Box
            sx={(theme) => ({
              background: theme.palette.background.paper,
              borderRadius: "8px",
              padding: 3,
              border: `1px solid ${theme.palette.divider}`,
            })}
          >
            <Grid container spacing={3}>
              <Grid item xs={12} sm={12} md={6}>
                <Stack direction="row" alignItems="center" spacing={1} sx={{ marginBottom: 3 }}>
                  <Box
                    sx={(theme) => ({
                      background: theme.palette.accent.main,
                      width: "6px",
                      height: "6px",
                    })}
                  />
                  {connectedWallet && (
                    <>
                      <Typography
                        sx={{
                          fontSize: "1.5rem",
                          fontWeight: 600,
                          marginBottom: "1rem",
                        }}
                      >
                        {shortHash(connectedWallet)}
                      </Typography>
                    </>
                  )}
                  {arnsName && (
                    <Chip
                      label={`${arnsName}.arns`}
                      size="small"
                      sx={() => ({
                        fontWeight: 600,
                        borderRadius: "4px",
                      })}
                    />
                  )}
                  <Chip
                    label="Your Wallet"
                    size="small"
                    sx={(theme) => ({
                      background: theme.palette.accent.main,
                      color: theme.palette.common.white,
                      borderRadius: "4px",
                    })}
                  />
                </Stack>
                <Stack direction={isMobile ? "column" : "row"} spacing={2}>
                  <StatsWidget>
                    <StatsWidgetLabel>Total PNL from Pools</StatsWidgetLabel>
                    <StatsWidgetValue
                      color={
                        totalPnlFromPools >= 0
                          ? theme.palette.success.main
                          : theme.palette.error.main
                      }
                    >
                      {numbro(totalPnlFromPools).formatCurrency({
                        thousandSeparated: true,
                        mantissa: 2,
                        average: true,
                      })}
                    </StatsWidgetValue>
                    <StatsWidgetChange
                      delta={last24HPnlChange > 0 ? 1 : -1}
                      value={numbro(last24HPnlChange).format({
                        thousandSeparated: true,
                        mantissa: 2,
                        average: true,
                      })}
                    >
                      in last 24 hrs
                    </StatsWidgetChange>
                  </StatsWidget>
                  <StatsWidget>
                    <StatsWidgetLabel>30 Day PnL</StatsWidgetLabel>
                    <StatsWidgetValue
                      color={
                        averagePnl >= 0 ? theme.palette.success.main : theme.palette.error.main
                      }
                    >
                      {numbro(averagePnl).formatCurrency({
                        thousandSeparated: true,
                        mantissa: 2,
                        average: true,
                      })}
                    </StatsWidgetValue>
                  </StatsWidget>
                  <StatsWidget>
                    <StatsWidgetLabel>Total 30 Day Avg. APY</StatsWidgetLabel>
                    <StatsWidgetValue
                      color={
                        averagePnlPercentage >= 0
                          ? theme.palette.success.main
                          : theme.palette.error.main
                      }
                    >
                      {numbro(averagePnlPercentage).format({
                        mantissa: 2,
                      })}
                      %
                    </StatsWidgetValue>
                  </StatsWidget>
                </Stack>
              </Grid>
              {!isMobile && (
                <Divider
                  orientation="vertical"
                  flexItem
                  sx={{ mr: "-1px", mt: "1rem", display: { xs: "none", md: "flex" } }}
                />
              )}
              <Grid item xs={12} sm={12} md={6}>
                <Typography
                  sx={{
                    fontSize: "1.25rem",
                    fontWeight: 400,
                    color: "text.secondary",
                  }}
                >
                  Total Assets Value
                </Typography>
                <Typography
                  sx={(theme) => ({
                    fontSize: "1.75rem",
                    color: theme.palette.accent.main,
                  })}
                >
                  {numbro(totalAssetsValue).formatCurrency({
                    thousandSeparated: true,
                    mantissa: 2,
                    average: true,
                  })}
                </Typography>

                <Box width="100%">
                  <TotalAssetsValueChart pools={data.pools} />
                </Box>
              </Grid>
            </Grid>
          </Box>
        </Stack>
        <Stack
          direction={isMobile ? "column" : "row"}
          justifyContent="space-between"
          alignItems={isMobile ? "flex-start" : "center"}
          spacing={2}
        >
          <Stack direction="row" alignItems="center" spacing={1}>
            <Box
              sx={(theme) => ({
                background: theme.palette.accent.main,
                width: "6px",
                height: "6px",
              })}
            />
            <Typography
              sx={{
                fontSize: "1.25rem",
                fontWeight: 400,
              }}
            >
              Your Pools
            </Typography>
          </Stack>

          <Box width={isMobile ? "100%" : 400}>
            <SearchPool handleChange={(e) => setSearch(e.target.value)} />
          </Box>
        </Stack>
        <PoolsStack pools={data?.pools} search={search} />
      </Stack>
    </PageWrapper>
  )
}
